// ────────────────────────────────────────────────────────────────────────────
// Shared deploy pipeline
//
// The full "deploy a ProcessedModelSchema" flow — upload files → create schema →
// create output params → link assets → create schedule rows → refetch — extracted
// from DynamicProcessedModelSchemaModal so both the 5-step modal and the AI-chat
// <model_deploy_proposal> card run the identical pipeline. UI-agnostic: progress is
// reported through optional `hooks` callbacks (addOp/updateOp/setStatus/onFileProgress).
// ────────────────────────────────────────────────────────────────────────────
import { generateClient } from "aws-amplify/api";
import { uploadData } from "aws-amplify/storage";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { scheduleConfigToRunSchedule, ScheduleConfig } from "../components/Schedule/scheduleConfig";
import { isOutputFieldKey, outputParameterTypeId } from "../utils/modelOutputUtils";
import { MODEL_SCHEMAS } from "../types/modelSchemas";
import { reconcileProcessedModelSchedules } from "../utils/processedModelScheduleSync";
import { organizationModelOwner, startBackfillWindow } from "../utils/historicalModelRuns";
import { effectiveConfig } from "../utils/historicalRuns";
import { createRunRequest } from "../services/historicalModelService";
import { asksForModelStorage, datasetPolicyFor, destinationOf, newDatasetNameOf } from "../utils/outputDestination";
import { generateDatasetId, generateUniqueSlug } from "../utils/datasetUtils";
import { createDatasetAction } from "./datasetSlice";
import { listDatasets } from "../graphql/queries";
import {
  postProcessedModelSchema,
  postProcessedModelOutputParam,
  createProcessedModelSchemaAsset,
  fetchProcessedModelSchemaById,
} from "./processedModelSchemaSlice";
import type { AppDispatch } from "./index";

// A deployable model template as stored in Redux (fieldDefinitions is a JSON string of field defs).
export interface ModelTemplate {
  id: string;
  name?: string;
  category?: string;
  description?: string;
  fieldDefinitions?: string;
  executionUrl?: string;
  version?: string;
  isActive?: boolean;
  [key: string]: unknown;
}

// One editor field value. Primitives arrive as { value }, files/assets carry extra metadata; a bare
// boolean (output toggle) or null are also valid. Field defs are dynamic admin JSON, hence `any`.
export interface SchemaFieldValue {
  value?: unknown;
  unit?: string;
  type?: string;
  file?: File;
  fileName?: string;
  fileType?: string;
  fileStatus?: string;
  uploadPath?: string;
  mode?: string;
  selectedAssetIds?: string[];
  location?: { type?: string; assetIds?: string[] };
  [key: string]: unknown;
}
export type SchemaFieldEntry = SchemaFieldValue | boolean | null;
export type SchemaFields = Record<string, SchemaFieldEntry>;
type FieldDefinitions = Record<string, any>;

// Minimal user shape the pipeline reads for owner resolution and S3 pathing.
export interface DeployUser {
  permissionDetails?: { id?: string; name?: string; cognitoIdentityId?: string };
  organizations?: { data?: Record<string, { name?: string; owner?: string }> };
  /** From the Cognito groups: an Admin's organization model is owned by the organization's owner, a member's by the
   * member (pre-rollout review 2026-10-17, R2 A). Unknown keeps the Admin convention. */
  isAdmin?: boolean;
}

// Optional UI callbacks so the pipeline can drive an operations log / progress bar without knowing
// which surface (modal wizard or chat card) is rendering it.
export interface DeployHooks {
  addOp?: (id: string, type: string, label: string, status: string) => void;
  updateOp?: (id: string, patch: { status: string; error?: string }) => void;
  setStatus?: (status: string) => void;
  onFileProgress?: (fieldName: string, pct: number) => void;
}

// Editor-shaped payload the deploy pipeline consumes (mirrors the modal's formData).
export interface DeployForm {
  name: string;
  description?: string;
  type?: string;
  typeId?: string;
  executionUrl?: string;
  schemaFields: SchemaFields;
  schedules?: ScheduleConfig[];
  isActive?: boolean;
  ownerType?: "personal" | "organization";
  selectedOrgKey?: string;
  dependency?: string | null;
  /** HISTORICAL from a historical template (copied by the wizard); null = forecast */
  modelKind?: string | null;
  /** HISTORICAL only: backfillFrom and autoBackfill from the wizard's History step */
  historicalSettings?: Record<string, unknown> | null;
  /** External forecast models only: when forecasts are expected (utils/deliveryExpectation.ts), already normalized */
  deliveryExpectation?: Record<string, unknown> | null;
}

interface DeployTarget {
  organizationId: string;
  owner: string;
  s3BasePath: string;
}

const errorMessage = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";

// Thrown by the deploy pipeline. `schemaCreated` lets callers tell "nothing was persisted" apart from
// "the schema record exists but a later step (outputs/schedules) failed" — so the UI can say so
// instead of implying the whole deploy was a no-op.
export class ProcessedModelDeployError extends Error {
  schemaCreated: boolean;
  constructor(message: string, schemaCreated = false) {
    super(message);
    this.name = "ProcessedModelDeployError";
    this.schemaCreated = schemaCreated;
  }
}

// True when a field carries a pending upload that still needs to be pushed to S3.
const isPendingUpload = (f: SchemaFieldValue | null | undefined): boolean => {
  if (!f) return false;
  const hasPendingPath = !!f.uploadPath && f.uploadPath.startsWith("pending-upload/");
  return (f.type === "file_reference" || hasPendingPath || f.fileStatus === "pendingUpload") && !!f.file;
};

// Build the initial schemaFields map from a template's field definitions, applying each field's
// default value / defaultChecked. Shared by the modal and the chat card so seeding stays identical.
export function buildInitialSchemaFields(fieldDefinitions?: FieldDefinitions | null): SchemaFields {
  const initialSchemaFields: SchemaFields = {};
  if (!fieldDefinitions) return initialSchemaFields;
  Object.entries(fieldDefinitions).forEach(([key, field]) => {
    let defaultValue = field.defaultValue ?? field.defaultChecked ?? null;
    // Multi-select enums store arrays — wrap a scalar default
    if (
      field.type === "enum" &&
      field.validation?.multiSelect &&
      defaultValue !== null &&
      !Array.isArray(defaultValue)
    ) {
      defaultValue = [defaultValue];
    }
    if (defaultValue !== null) {
      const entry: SchemaFieldValue = { value: defaultValue };
      const allowedUnits = field.validation?.allowedUnits || field.allowedUnits;
      if (field.unit) entry.unit = field.unit;
      else if (allowedUnits?.length > 0) entry.unit = allowedUnits[0];
      initialSchemaFields[key] = entry;
    } else {
      initialSchemaFields[key] = null;
    }
  });
  return initialSchemaFields;
}

// Human-readable schema id: {name}-{type}-{uuid8}. Matches the modal's previous generator.
export function generateProcessedModelSchemaId(name = "", type = ""): string {
  const cleanName = String(name).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const cleanType = String(type).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const uuid = crypto.randomUUID().split("-")[0];
  return `${cleanName}-${cleanType}-${uuid}`;
}

// Resolve owner, organizationId and the literal S3 base path from the owner selection. The path is
// signed by the caller's own Cognito identity at upload time (custom path-based IAM), so this must
// run in the logged-in user's session.
export function buildProcessedModelDeployTarget({
  user,
  ownerType,
  selectedOrgKey,
  schemaId,
}: {
  user?: DeployUser;
  ownerType?: string;
  selectedOrgKey?: string;
  schemaId: string;
}): DeployTarget {
  if (ownerType === "organization" && selectedOrgKey) {
    return {
      organizationId: selectedOrgKey,
      owner: organizationModelOwner({
        isAdmin: user?.isAdmin,
        organizationOwner: user?.organizations?.data?.[selectedOrgKey]?.owner,
        selfId: user?.permissionDetails?.id,
      }),
      s3BasePath: `organizationprivate/${selectedOrgKey}/processedModelSchema/${schemaId}/`,
    };
  }
  return {
    organizationId: "",
    owner: user?.permissionDetails?.id || "",
    s3BasePath: `private/${user?.permissionDetails?.cognitoIdentityId}/processedModelSchema/${schemaId}/`,
  };
}

function resolveFieldDefinitions(template?: ModelTemplate, typeId?: string): FieldDefinitions {
  if (template?.fieldDefinitions) {
    try {
      return JSON.parse(template.fieldDefinitions);
    } catch (e) {
      console.error("Error parsing field definitions:", e);
      return {};
    }
  }
  return (typeId && MODEL_SCHEMAS[typeId]?.fields) || {};
}

// Annotate each schemaField value with its field type and rewrite pending upload paths to the real
// S3 key. Strips the raw File object (avoids circular refs / bloated payloads) — callers re-attach it
// before uploading. Mirrors the modal's previous injectTypesToSchemaFields exactly.
export function injectTypesToSchemaFields(
  fields: SchemaFields,
  template?: ModelTemplate,
  templateId?: string,
  s3BasePath: string | null = null
): Record<string, SchemaFieldValue> {
  const fieldDefinitions = resolveFieldDefinitions(template, templateId);
  const typedFields: Record<string, SchemaFieldValue> = {};
  Object.entries(fields).forEach(([key, value]) => {
    const fieldDef = fieldDefinitions[key];
    const type = fieldDef?.type || "unknown";

    if (value === null || value === undefined) {
      typedFields[key] = { value: null, type };
      return;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      const cleanValue: SchemaFieldValue = { ...value };
      if (s3BasePath && cleanValue.uploadPath && cleanValue.uploadPath.startsWith("pending-upload/")) {
        cleanValue.uploadPath = `${s3BasePath}input/${cleanValue.fileName}`;
        if (cleanValue.file) cleanValue.fileStatus = "pendingUpload";
      }
      if ("file" in cleanValue) delete cleanValue.file;
      typedFields[key] = { ...cleanValue, type };
    } else {
      typedFields[key] = { value, type };
    }
  });
  return typedFields;
}

// Drop output fields (persisted separately as ProcessedModelOutputParam records) before saving the
// schema's schemaFields blob.
export function removeOutputFieldsFromSchema(
  schemaFields: Record<string, SchemaFieldValue>
): Record<string, SchemaFieldValue> {
  const filteredFields: Record<string, SchemaFieldValue> = {};
  Object.entries(schemaFields).forEach(([key, value]) => {
    if (!isOutputFieldKey(key)) filteredFields[key] = value;
  });
  return filteredFields;
}

// Upload every pending file_reference / 2d_array file to its S3 input path, returning schemaFields
// with each uploaded field's status/path updated. Throws on the first failed upload so the caller can
// abort before creating the record. Progress is surfaced via optional hooks.
export async function uploadProcessedModelSchemaFiles(
  schemaFields: Record<string, SchemaFieldValue>,
  s3BasePath: string,
  hooks: DeployHooks = {}
): Promise<Record<string, SchemaFieldValue>> {
  const { addOp, updateOp, setStatus, onFileProgress } = hooks;
  const filesToUpload: Array<{ fieldName: string; fieldData: SchemaFieldValue; s3Key: string }> = [];

  Object.entries(schemaFields).forEach(([fieldName, fieldData]) => {
    if (isPendingUpload(fieldData)) {
      filesToUpload.push({ fieldName, fieldData, s3Key: `${s3BasePath}input/${fieldData.fileName}` });
    }
  });

  if (filesToUpload.length === 0) return schemaFields;

  const updated = { ...schemaFields };
  setStatus?.(`Uploading ${filesToUpload.length} file(s)...`);

  for (let i = 0; i < filesToUpload.length; i++) {
    const { fieldName, fieldData, s3Key } = filesToUpload[i];
    const opId = `file-${fieldName}-${Date.now()}`;
    addOp?.(opId, "file", `File: ${fieldData.fileName}`, "in_progress");
    setStatus?.(`Uploading ${fieldName} file (${i + 1}/${filesToUpload.length})...`);
    onFileProgress?.(fieldName, 0);

    try {
      const uploadRes = await uploadData({
        path: s3Key,
        data: fieldData.file as File,
        options: {
          contentType: fieldData.fileType,
          onProgress: ({ transferredBytes, totalBytes }: { transferredBytes: number; totalBytes?: number }) => {
            if (totalBytes) onFileProgress?.(fieldName, Math.round((transferredBytes / totalBytes) * 100));
          },
        },
      }).result;

      if (!uploadRes?.path) {
        updateOp?.(opId, { status: "error", error: "Upload did not return a valid path" });
        throw new Error(`Failed to upload ${fieldName} file. Upload did not return a valid path.`);
      }

      onFileProgress?.(fieldName, 100);
      updateOp?.(opId, { status: "success" });
      updated[fieldName] = { ...updated[fieldName], fileStatus: "uploadedSuccessful", uploadPath: uploadRes.path };
    } catch (fileError) {
      updateOp?.(opId, { status: "error", error: errorMessage(fileError) });
      throw fileError;
    }
  }

  setStatus?.(`✅ All ${filesToUpload.length} file(s) uploaded successfully!`);
  return updated;
}

// Upload files, type-annotate, and strip output fields — producing the schemaFields blob persisted on
// the schema record. Shared by the create pipeline and the modal's edit path.
export async function prepareSchemaFieldsForSave({
  schemaFields,
  template,
  typeId,
  s3BasePath,
  hooks = {},
}: {
  schemaFields: SchemaFields;
  template?: ModelTemplate;
  typeId?: string;
  s3BasePath: string;
  hooks?: DeployHooks;
}): Promise<Record<string, SchemaFieldValue>> {
  const fieldsWithTypes = injectTypesToSchemaFields(schemaFields, template, typeId, s3BasePath);
  // Re-attach File objects stripped by injectTypesToSchemaFields so the upload step can read them.
  Object.keys(fieldsWithTypes).forEach((key) => {
    const raw = schemaFields[key];
    if (raw && typeof raw === "object" && raw.file) fieldsWithTypes[key].file = raw.file;
  });

  const hasFilesToUpload = Object.values(fieldsWithTypes).some(isPendingUpload);

  let uploadedSchemaFields: Record<string, SchemaFieldValue> = fieldsWithTypes;
  if (hasFilesToUpload) {
    uploadedSchemaFields = await uploadProcessedModelSchemaFiles(fieldsWithTypes, s3BasePath, hooks);
  }

  const typedSchemaFields = injectTypesToSchemaFields(uploadedSchemaFields, template, typeId, s3BasePath);
  return removeOutputFieldsFromSchema(typedSchemaFields);
}

/** Queue a new input-less HISTORICAL model's history backfill (startBackfillWindow). Best effort: the model is
 * already made, so a failure is logged and the user can still use Re-run a range. */
async function queueStartBackfill({
  schemaId,
  template,
  historicalSettings,
  hooks = {},
}: {
  schemaId: string;
  template?: ModelTemplate;
  historicalSettings?: Record<string, unknown> | null;
  hooks?: DeployHooks;
}) {
  if (template?.modelKind !== "HISTORICAL") return;
  let fieldDefinitions: FieldDefinitions = {};
  let rawConfig: any = template.historicalConfig ?? null;
  try {
    fieldDefinitions = template.fieldDefinitions ? JSON.parse(template.fieldDefinitions) : {};
    rawConfig = typeof rawConfig === "string" ? JSON.parse(rawConfig) : rawConfig;
  } catch {
    return;
  }
  const window = startBackfillWindow({
    fieldDefinitions,
    settings: historicalSettings as { backfillFrom?: string; autoBackfill?: boolean },
    config: effectiveConfig(rawConfig, null),
    now: new Date().toISOString(),
  });
  if (!window) return;
  const opId = `backfill-${schemaId}`;
  hooks.addOp?.(opId, "backfill", `History backfill: ${window.start.slice(0, 10)} → today`, "in_progress");
  try {
    await createRunRequest({ processedModelSchemaId: schemaId, triggerType: "BACKFILL", windowStart: window.start, windowEnd: window.end });
    hooks.updateOp?.(opId, { status: "success" });
  } catch (err) {
    hooks.updateOp?.(opId, { status: "error", error: `${errorMessage(err)} — use Re-run a range on the model` });
  }
}

/** A live dataset this model already made under this name (a partial deploy's leftover), or null. Best effort. */
async function findModelDataset(sourceModelId: string, name: string): Promise<string | null> {
  try {
    const client = generateClient();
    const { data } = (await client.graphql({
      query: listDatasets,
      variables: { filter: { sourceModelId: { eq: sourceModelId }, name: { eq: name }, deletedAt: { attributeExists: false } }, limit: 50 },
    })) as { data?: { listDatasets?: { items?: Array<{ id: string } | null> } } };
    return data?.listDatasets?.items?.find(Boolean)?.id ?? null;
  } catch {
    return null;
  }
}

/** A HISTORICAL output's storage: the dataset the wizard pointed it at (an existing one, or the one made for it on
 * deploy). A model never stores historical data itself; a value still asking for "Model output" (SERIES) is refused. */
function historicalStorageOf(value: unknown, fieldKey: string, newDatasetIds: Record<string, string>): Record<string, unknown> {
  if (asksForModelStorage(value)) throw new Error(`Output ${fieldKey}: a historical model's output goes to a dataset; choose a new or an existing one`);
  if (destinationOf(value) === "DATASET") {
    const datasetId = (value as { datasetId?: string }).datasetId;
    if (!datasetId) throw new Error("An output set to write an existing dataset has no dataset chosen");
    return { storageMode: "DATASET", targetDatasetId: datasetId };
  }
  const datasetId = newDatasetIds[fieldKey];
  if (!datasetId) throw new Error(`Output ${fieldKey}: its new dataset was not created`);
  return { storageMode: "DATASET", targetDatasetId: datasetId };
}

/** The ticked outputs of a HISTORICAL template that get a new dataset on deploy: field key → the dataset's name
 * (what was typed, else "<model> · <output label>"). One dataset per output. */
export function newDatasetNamesFor(template: ModelTemplate | undefined, schemaFields: SchemaFields, schemaName: string): Record<string, string> {
  if (template?.modelKind !== "HISTORICAL" || !template?.fieldDefinitions) return {};
  const fieldDefinitions: FieldDefinitions = JSON.parse(template.fieldDefinitions);
  const names: Record<string, string> = {};
  Object.entries(schemaFields).forEach(([fieldKey, value]) => {
    const definition = fieldDefinitions[fieldKey];
    const isSelected = value === true || (value && typeof value === "object" && value.value === true);
    if (!isSelected || !definition || !isOutputFieldKey(fieldKey, definition)) return;
    if (asksForModelStorage(value) || destinationOf(value) !== "NEW_DATASET") return;
    names[fieldKey] = newDatasetNameOf(value, schemaName, definition.label || fieldKey.replace(/^output[-_]?/i, ""));
  });
  return names;
}

/** The merge policy a HISTORICAL template's runs need (historicalConfig.mergePolicy), parsed from the template row. */
function templateMergePolicy(template: ModelTemplate | undefined): string | null {
  try {
    const raw = (template as { historicalConfig?: unknown } | undefined)?.historicalConfig;
    const config = typeof raw === "string" ? JSON.parse(raw) : raw;
    return (config as { mergePolicy?: string } | null)?.mergePolicy ?? null;
  } catch {
    return null;
  }
}

/**
 * Make the datasets a HISTORICAL model's outputs write (one per output, field key → dataset id), before the output
 * rows that point at them. Each is a curated HISTORICAL dataset with the model's owner and organization, the
 * duplicate policy the template's runs need, and the model recorded as its source ("Written by <model>").
 */
export async function createOutputDatasets({
  names,
  organizationId,
  sourceModelId,
  duplicatePolicy,
  dispatch,
  hooks = {},
}: {
  names: Record<string, string>;
  organizationId: string | null;
  sourceModelId: string;
  duplicatePolicy: "REPLACE_WINDOW" | "LATEST_CELL";
  dispatch: AppDispatch;
  hooks?: DeployHooks;
}): Promise<Record<string, string>> {
  const { addOp, updateOp, setStatus } = hooks;
  const created: Record<string, string> = {};
  const entries = Object.entries(names);
  if (!entries.length) return created;
  const authUser = await getCurrentUser();
  const cognitoIdentityId = organizationId ? null : (await fetchAuthSession()).identityId;
  if (!organizationId && !cognitoIdentityId) throw new Error("Unable to get your identity for a personal dataset's storage.");
  for (const [fieldKey, name] of entries) {
    const opId = `dataset-${fieldKey}`;
    addOp?.(opId, "dataset", `Dataset: ${name}`, "in_progress");
    setStatus?.(`Creating dataset: ${name}...`);
    // A deploy that failed after this step left the dataset behind, stamped with this model: take it rather than
    // make a second one on the retry
    const orphan = await findModelDataset(sourceModelId, name);
    if (orphan) {
      updateOp?.(opId, { status: "success" });
      created[fieldKey] = orphan;
      continue;
    }
    const id = generateDatasetId("HISTORICAL", name);
    const slug = await generateUniqueSlug(organizationId || "", name);
    // createDatasetAction's input is untyped JS (HistoricalDatasetModal builds the same shape)
    const result: any = await dispatch(
      (createDatasetAction as any)({
        id,
        slug,
        name,
        description: "Written by a historical model's runs",
        datasetType: "HISTORICAL",
        fileFormat: "CSV",
        duplicatePolicy,
        sourceModelId,
        owner: authUser.userId,
        userId: organizationId ? null : authUser.userId,
        organizationId: organizationId || null,
        cognitoIdentityId,
      } as any)
    );
    if (!createDatasetAction.fulfilled.match(result)) {
      const message = result.error?.message || result.payload || "Failed to create dataset";
      updateOp?.(opId, { status: "error", error: String(message) });
      throw new Error(`Couldn't create dataset "${name}": ${message}`);
    }
    updateOp?.(opId, { status: "success" });
    created[fieldKey] = result.payload.id;
  }
  return created;
}

// Create a ProcessedModelOutputParam record for every selected output field. Throws on the first
// failure. Reads the RAW (untyped) schemaFields to detect ticked outputs, matching the modal.
export async function createProcessedModelOutputParameters({
  schemaId,
  organizationId,
  owner,
  s3BasePath,
  template,
  schemaFields,
  schemaName,
  typeId,
  dispatch,
  hooks = {},
  newDatasetIds = {},
}: {
  schemaId: string;
  organizationId: string;
  owner: string;
  s3BasePath: string;
  template?: ModelTemplate;
  schemaFields: SchemaFields;
  schemaName: string;
  typeId?: string;
  dispatch: AppDispatch;
  hooks?: DeployHooks;
  /** The datasets createOutputDatasets made, by output field key */
  newDatasetIds?: Record<string, string>;
}): Promise<any[]> {
  const { addOp, updateOp, setStatus } = hooks;
  if (!template?.fieldDefinitions) return [];
  const fieldDefinitions: FieldDefinitions = JSON.parse(template.fieldDefinitions);

  const outputsToCreate: Array<Record<string, unknown>> = [];
  Object.entries(schemaFields).forEach(([fieldKey, value]) => {
    const isSelected = value === true || (value && typeof value === "object" && value.value === true);
    const fieldDefinition = fieldDefinitions[fieldKey];
    if (isSelected && fieldDefinition && isOutputFieldKey(fieldKey, fieldDefinition)) {
      const outputName = fieldKey.replace(/^output[-_]?/i, "");
      const kebabCaseName = outputName.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
      const cleanSchemaName = String(schemaName).replace(/[^a-zA-Z0-9]/g, "").toLowerCase().substring(0, 15);
      const cleanOutputName = kebabCaseName.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase().substring(0, 20);
      const randomSuffix = crypto.randomUUID().split("-")[0].substring(0, 6);
      const outputId = `${cleanSchemaName}-${cleanOutputName}-${randomSuffix}`;
      const unit = value && typeof value === "object" && value.unit ? value.unit : fieldDefinition.unit || null;
      const parameterTypeId = outputParameterTypeId(fieldKey, typeId || "");
      outputsToCreate.push({
        id: outputId,
        name: outputName,
        description: fieldDefinition.description || fieldDefinition.label,
        unit,
        schemaId,
        organizationId,
        owner,
        outputLocation: `${s3BasePath}output/${outputId}`,
        parameterTypeId,
        active: true,
        // A historical model's outputs are rows loaded into a dataset (docs/architecture/HISTORICAL_DATA_RUNNERS.md)
        ...(template?.modelKind === "HISTORICAL" ? historicalStorageOf(value, fieldKey, newDatasetIds) : {}),
      });
    }
  });

  const createdParams = [];
  for (let i = 0; i < outputsToCreate.length; i++) {
    const outputParam = outputsToCreate[i];
    const opId = `output-${outputParam.id}`;
    addOp?.(opId, "output", `Output: ${outputParam.name} (${outputParam.id})`, "in_progress");
    setStatus?.(`Creating output parameter: ${outputParam.name} (${i + 1}/${outputsToCreate.length})...`);

    const resultAction = await dispatch(postProcessedModelOutputParam(outputParam));
    if (postProcessedModelOutputParam.fulfilled.match(resultAction)) {
      createdParams.push(resultAction.payload);
      updateOp?.(opId, { status: "success" });
    } else {
      updateOp?.(opId, { status: "error", error: "Failed to create output parameter" });
      throw new Error(`Failed to create output parameter: ${outputParam.name}`);
    }
  }

  return createdParams;
}

// Create ProcessedModelSchemaAssets join rows for asset_boundary/asset_location (asset_reference mode)
// and forecast (assetPoint/assetGeojson) fields. Best-effort per link — one failure doesn't abort the rest.
export async function linkProcessedModelSchemaAssets({
  schemaId,
  organizationId,
  template,
  schemaFields,
  dispatch,
  hooks = {},
}: {
  schemaId: string;
  organizationId: string;
  template?: ModelTemplate;
  schemaFields: SchemaFields;
  dispatch: AppDispatch;
  hooks?: DeployHooks;
}): Promise<any[]> {
  const { addOp, updateOp } = hooks;
  if (!template?.fieldDefinitions) return [];
  const fieldDefinitions: FieldDefinitions = JSON.parse(template.fieldDefinitions);

  const linkableFields = Object.entries(fieldDefinitions).filter(
    ([, field]) =>
      field.type === "asset_boundary" || field.type === "asset_location" || field.type === "forecast"
  );
  if (linkableFields.length === 0) return [];

  const createdLinks: any[] = [];
  const processedAssetIds = new Set<string>();

  for (const [fieldKey, fieldDef] of linkableFields) {
    const fieldValue = schemaFields[fieldKey];
    if (!fieldValue || typeof fieldValue !== "object") continue;

    let assetIdsToLink: string[] = [];
    if (
      (fieldDef.type === "asset_boundary" || fieldDef.type === "asset_location") &&
      fieldValue.mode === "asset_reference" &&
      fieldValue.selectedAssetIds
    ) {
      assetIdsToLink = fieldValue.selectedAssetIds;
    }
    if (fieldDef.type === "forecast" && fieldValue.location) {
      const locationType = fieldValue.location.type;
      if ((locationType === "assetPoint" || locationType === "assetGeojson") && fieldValue.location.assetIds) {
        assetIdsToLink = fieldValue.location.assetIds;
      }
    }

    for (const assetId of assetIdsToLink) {
      if (processedAssetIds.has(assetId)) continue;
      processedAssetIds.add(assetId);

      const opId = `asset-link-${assetId}`;
      addOp?.(opId, "asset_link", `Asset Link: ${assetId}`, "in_progress");
      try {
        const resultAction = await dispatch(
          createProcessedModelSchemaAsset({
            processedModelSchemaID: schemaId,
            assetID: assetId,
            organizationId: organizationId || undefined,
          })
        );
        if (createProcessedModelSchemaAsset.fulfilled.match(resultAction)) {
          createdLinks.push(resultAction.payload);
          updateOp?.(opId, { status: "success" });
        } else {
          updateOp?.(opId, { status: "error", error: "Failed to create asset link" });
          console.error(`Failed to create asset link: ${assetId}`, resultAction.error);
        }
      } catch (error) {
        updateOp?.(opId, { status: "error", error: errorMessage(error) });
        console.error(`Error creating asset link for ${assetId}:`, error);
      }
    }
  }

  return createdLinks;
}

// TODO(backend): the membership-tier cap is applied by callers (useProcessedModelLimit) — this pipeline deploys whatever it's handed, so backend enforcement is still required.
/**
 * Deploy a new ProcessedModelSchema end-to-end from an editor-shaped `form`. Runs the full pipeline
 * (upload → create schema → outputs → asset links → schedules → refetch) and returns the new
 * { schemaId }. Throws on hard failures (upload, schema create, output create) so callers can surface
 * the error; asset-link and schedule failures are best-effort. Pass `hooks` to drive an operations
 * log / progress UI.
 */
export async function deployProcessedModelSchema({
  dispatch,
  user,
  template,
  form,
  hooks = {},
  schemaId: providedId = undefined,
}: {
  dispatch: AppDispatch;
  user?: DeployUser;
  template?: ModelTemplate;
  form: DeployForm;
  hooks?: DeployHooks;
  schemaId?: string;
}): Promise<DeployTarget & { schemaId: string }> {
  const { addOp, updateOp, setStatus } = hooks;
  const schemaId = providedId || generateProcessedModelSchemaId(form.name, form.type);
  const { organizationId, owner, s3BasePath } = buildProcessedModelDeployTarget({
    user,
    ownerType: form.ownerType,
    selectedOrgKey: form.selectedOrgKey,
    schemaId,
  });

  // Upload files, then build the typed, output-stripped fields for persistence.
  const filteredSchemaFields = await prepareSchemaFieldsForSave({
    schemaFields: form.schemaFields,
    template,
    typeId: form.typeId,
    s3BasePath,
    hooks,
  });

  // Create the schema record. The first schedule is stored as a representative runSchedule; the
  // authoritative schedule rows are reconciled below (passing { schema } only skips the thunk's
  // own reconcile so the hooks-logged reconcile is the single source).
  const recordOpId = `record-${schemaId}`;
  addOp?.(recordOpId, "record", `Model Schema: ${schemaId}`, "in_progress");
  setStatus?.("Creating model schema record...");
  const representativeRunSchedule = form.schedules && form.schedules.length
    ? scheduleConfigToRunSchedule(form.schedules[0])
    : { firstRunTime: "", frequency: "ONCE", cronExpression: "", timezone: "UTC" };

  const newSchema = {
    id: schemaId,
    name: form.name,
    description: form.description,
    type: form.type,
    schemaType: form.typeId,
    version: template?.version || "1.0",
    executionUrl: form.executionUrl || "",
    schemaFields: JSON.stringify(filteredSchemaFields),
    runSchedule: JSON.stringify(representativeRunSchedule),
    isActive: form.isActive,
    activeStatus: form.isActive ? "ACTIVE" : "INACTIVE",
    organizationId,
    owner,
    s3BasePath,
    dependency: form.dependency || null,
    ...(form.modelKind ? { modelKind: form.modelKind } : {}),
    ...(form.historicalSettings ? { historicalSettings: JSON.stringify(form.historicalSettings) } : {}),
    ...(form.deliveryExpectation ? { deliveryExpectation: JSON.stringify(form.deliveryExpectation) } : {}),
  };

  const resultAction = await dispatch(postProcessedModelSchema({ schema: newSchema }));
  if (!postProcessedModelSchema.fulfilled.match(resultAction)) {
    // Record never persisted — schemaCreated stays false so callers say "failed to create".
    const message = resultAction.error?.message || "Unknown error";
    updateOp?.(recordOpId, { status: "error", error: message });
    throw new ProcessedModelDeployError(`Failed to create model schema: ${message}`, false);
  }
  updateOp?.(recordOpId, { status: "success" });

  // From here the schema record exists: a failure means a partial deploy, not a no-op. Re-tag any
  // error with schemaCreated:true so the UI reports it accurately instead of "failed to create".
  try {
    // The new datasets the outputs write (before the output rows that point at them)
    const newDatasetIds = await createOutputDatasets({
      names: newDatasetNamesFor(template, form.schemaFields, form.name),
      organizationId: organizationId || null,
      sourceModelId: schemaId,
      duplicatePolicy: datasetPolicyFor(templateMergePolicy(template)),
      dispatch,
      hooks,
    });

    // Output parameters (throws on failure — schema already created, surfaced to the caller).
    setStatus?.("Creating output parameters...");
    await createProcessedModelOutputParameters({
      newDatasetIds,
      schemaId,
      organizationId,
      owner,
      s3BasePath,
      template,
      schemaFields: form.schemaFields,
      schemaName: form.name,
      typeId: form.typeId,
      dispatch,
      hooks,
    });

    // Asset links (best-effort inside).
    setStatus?.("Linking assets...");
    await linkProcessedModelSchemaAssets({
      schemaId,
      organizationId,
      template,
      schemaFields: form.schemaFields,
      dispatch,
      hooks,
    });

    // A model with no dataset input (a data runner) backfills its history now; queued before the schedule so the
    // whole backfill runs ahead of the first scheduled run, which then carries on from where it ended
    await queueStartBackfill({ schemaId, template, historicalSettings: form.historicalSettings, hooks });

    // Schedule rows.
    setStatus?.("Creating schedules...");
    await reconcileProcessedModelSchedules(
      schemaId,
      form.schedules,
      { isActive: form.isActive, owner, organizationId },
      { addOp, updateOp }
    );

    // Refetch to hydrate relationships.
    const refreshOpId = `refresh-${schemaId}`;
    addOp?.(refreshOpId, "refresh", "Refreshing schema data", "in_progress");
    setStatus?.("Refreshing schema data...");
    await dispatch(fetchProcessedModelSchemaById(schemaId));
    updateOp?.(refreshOpId, { status: "success" });
  } catch (e) {
    throw new ProcessedModelDeployError(
      `Model schema "${form.name}" was created, but a later step failed: ${errorMessage(e)}`,
      true
    );
  }

  setStatus?.("✅ Model schema created successfully!");
  return { schemaId, organizationId, owner, s3BasePath };
}
